/**
 * Saudi banking constants, validators, and option builders for banque module pages.
 */

export const SAUDI_BANK_OPTIONS = [
  { value: 'SNB', label: 'البنك الأهلي السعودي (SNB)' },
  { value: 'RJHI', label: 'مصرف الراجحي (RJHI)' },
  { value: 'RIBL', label: 'بنك الرياض (RIBL)' },
  { value: 'BSFR', label: 'البنك السعودي الفرنسي (BSFR)' },
  { value: 'SAIB', label: 'البنك السعودي للاستثمار (SAIB)' },
  { value: 'BLAD', label: 'بنك البلاد (BLAD)' },
  { value: 'BJAZ', label: 'بنك الجزيرة (BJAZ)' },
  { value: 'ANB', label: 'البنك العربي الوطني (ANB)' },
  { value: 'GIB', label: 'بنك الخليج الدولي — السعودية (GIB)' },
  { value: 'INMA', label: 'مصرف الإنماء (INMA)' },
];

/** Primary Saudi banks for distribution charts (top 3 + other). */
export const SAUDI_BANK_CODES_PRIMARY = ['SNB', 'RJHI', 'INMA'];

/** Sample Saudi IBAN for imports / placeholders. */
export const SAUDI_SAMPLE_IBAN = 'SA0380000000608010167519';

/** Internal fee-type keys persisted in localStorage. */
export const SAUDI_FEE_TYPE_KEYS = {
  sarie_transfer: 'fee_type_sarie',
  sadad_payment: 'fee_type_sadad',
  account_maintenance: 'fee_type_account',
  international_transfer: 'fee_type_international',
  checkbook: 'fee_type_checkbook',
  check_processing: 'fee_type_check',
  card_commission: 'fee_type_card',
  standing_order: 'fee_type_standing_order',
  direct_debit: 'fee_type_direct_debit',
};

export const SAUDI_FEE_TYPE_VALUES = Object.keys(SAUDI_FEE_TYPE_KEYS);

/** Payment order method keys (SARIE, SADAD, etc.). */
export const SAUDI_PAYMENT_ORDER_METHODS = [
  { value: 'sarie', labelKey: 'payment_method_sarie' },
  { value: 'sadad', labelKey: 'payment_method_sadad' },
  { value: 'direct_debit', labelKey: 'payment_method_direct_debit' },
  { value: 'standing_order', labelKey: 'payment_method_standing_order' },
  { value: 'wire', labelKey: 'payment_method_wire' },
];

/** SAMA purpose-of-payment codes (common corporate codes). */
export const SAUDI_PURPOSE_CODES = [
  { value: '01', labelKey: 'purpose_salary' },
  { value: '02', labelKey: 'purpose_supplier' },
  { value: '03', labelKey: 'purpose_rent' },
  { value: '04', labelKey: 'purpose_utilities' },
  { value: '05', labelKey: 'purpose_loan' },
  { value: '99', labelKey: 'purpose_other' },
];

export const SAUDI_PAYMENT_PRIORITIES = [
  { value: 'normal', labelKey: 'priority_normal' },
  { value: 'urgent', labelKey: 'priority_urgent' },
];

/** Amount tier breakpoints in SAR for checks and fees. */
export const SAUDI_CHECK_AMOUNT_TIERS = {
  over100k: 100_000,
  over50k: 50_000,
};

export const SAUDI_FEE_AMOUNT_TIERS = {
  high: 100,
  medium: 25,
};

/** Validate Saudi IBAN: SA + 22 digits (24 chars total), mod-97 checksum. */
export { validateSaudiIban, normalizeSaudiIban } from '../../shared/utils/ibanValidation';

/** Saudi check number: 6–10 digits, or entity code CHQ-001. */
export function validateSaudiCheckNumber(num) {
  const raw = String(num || '').trim();
  if (/^[A-Z]{2,4}-\d+$/i.test(raw)) return true;
  return /^\d{6,10}$/.test(raw);
}

/** Detect post-dated check (due date after issue date). */
export function isPostDatedCheck(issueDate, dueDate) {
  if (!issueDate || !dueDate) return false;
  return new Date(dueDate) > new Date(issueDate);
}

export function buildSaudiBankSelectOptions() {
  return SAUDI_BANK_OPTIONS.map((b) => ({ value: b.value, label: b.label, isDefault: true }));
}

/** Bank name options from live API accounts (+ existing row values). Prefer over static SAUDI_BANK_OPTIONS in dialogs. */
export function buildBankSelectOptionsFromAccounts(accounts = [], extraNames = []) {
  const seen = new Set();
  const options = [];
  (accounts || []).forEach((account) => {
    const name = String(account.bank_name || account.bankName || account.bank || '').trim();
    if (!name || seen.has(name)) return;
    seen.add(name);
    options.push({ value: name, label: name, isDefault: true });
  });
  (extraNames || []).forEach((raw) => {
    const name = String(raw || '').trim();
    if (!name || seen.has(name)) return;
    seen.add(name);
    options.push({ value: name, label: name, isDefault: true });
  });
  return options;
}

/** Beneficiary picker: clients + suppliers + names already used in payment orders. */
export function buildPaymentBeneficiaryOptions({ clients = [], suppliers = [], orders = [] } = {}) {
  const seen = new Set();
  const options = [];
  const push = (label) => {
    const name = String(label || '').trim();
    if (!name || seen.has(name)) return;
    seen.add(name);
    options.push({ value: name, label: name, isDefault: true });
  };

  (clients || []).forEach((c) => push(c.name || c.company || c.company_name));
  (suppliers || []).forEach((s) => push(s.company_name || s.name || s.company));
  (orders || []).forEach((o) => push(o.beneficiaire || o.beneficiary_name));

  return options;
}

export function buildFeeTypeSelectOptions(p) {
  return SAUDI_FEE_TYPE_VALUES.map((key) => ({
    value: key,
    label: p(SAUDI_FEE_TYPE_KEYS[key]),
    isDefault: true,
  }));
}

export function feeTypeLabel(type, p) {
  return SAUDI_FEE_TYPE_KEYS[type] ? p(SAUDI_FEE_TYPE_KEYS[type]) : type;
}

/** Default mock bank fees in SAR for Saudi context. */
export function getSaudiDefaultBankFees() {
  return [
    { id: 1, date: '2025-01-05', banque: 'SNB', type: 'international_transfer', montant: 57.5, reference: 'SWIFT-2025-001', description: 'رسوم تحويل دولي SWIFT' },
    { id: 2, date: '2025-01-04', banque: 'RJHI', type: 'card_commission', montant: 25, reference: 'CARD-2025-002', description: 'عمولة بطاقة مدى الشهرية' },
    { id: 3, date: '2025-01-03', banque: 'ANB', type: 'account_maintenance', montant: 35, reference: 'ACCT-2025-003', description: 'رسوم صيانة حساب ربع سنوية' },
    { id: 4, date: '2025-01-02', banque: 'SNB', type: 'sarie_transfer', montant: 0, reference: 'SARIE-2025-004', description: 'تحويل SARIE محلي — مجاني' },
    { id: 5, date: '2025-01-01', banque: 'INMA', type: 'checkbook', montant: 40, reference: 'CHQ-2025-005', description: 'إصدار دفتر شيكات جديد' },
    { id: 6, date: '2024-12-28', banque: 'BJAZ', type: 'check_processing', montant: 10, reference: 'CLR-2024-156', description: 'رسوم مقاصة شيك' },
    { id: 7, date: '2024-12-27', banque: 'RIBL', type: 'sadad_payment', montant: 0, reference: 'SADAD-2024-089', description: 'دفع فاتورة عبر SADAD — بدون رسوم' },
    { id: 8, date: '2024-12-26', banque: 'BLAD', type: 'standing_order', montant: 5, reference: 'STO-2024-012', description: 'رسوم أمر دفع مستديم' },
  ];
}
